import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { defaultAlert } from '../../../../utils/alert';

interface ApplyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (proposedSalary: string) => void;
    currentSalary: number;
    totalDays: number;
    negotiable: boolean;
    workType?: string;
    workStartTime?: string;
    workEndTime?: string;
    proposalRangeMinus?: number;
    proposalRangePlus?: number;
}

export const ApplyModal: React.FC<ApplyModalProps> = ({
                                                          isOpen, onClose, onConfirm, currentSalary, totalDays, negotiable,
                                                          workType, workStartTime, workEndTime,
                                                          proposalRangeMinus = 0, proposalRangePlus = 0,
                                                      }) => {
    const [proposedSalary, setProposedSalary] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const modalRoot = document.getElementById('modal-root');

    React.useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
            setProposedSalary('');
        }
        return () => document.body.classList.remove('modal-open');
    }, [isOpen]);

    if (!isOpen || !modalRoot) return null;

    const isHourly = workType === 'HOURLY';

    // 협의가능일 때 허용 제안 범위 계산
    const minAllowed = Math.floor(currentSalary * (1 - proposalRangeMinus / 100));
    const maxAllowed = Math.floor(currentSalary * (1 + proposalRangePlus / 100));
    const hasRange = negotiable && (proposalRangeMinus > 0 || proposalRangePlus > 0);

    const getDailyHours = (): number => {
        if (!workStartTime || !workEndTime) return 0;
        const [startH, startM] = workStartTime.split(':').map(Number);
        const [endH, endM] = workEndTime.split(':').map(Number);
        return (endH * 60 + endM - (startH * 60 + startM)) / 60;
    };

    const dailyHours = isHourly ? getDailyHours() : 0;
    const salaryLabel = isHourly ? '시급' : '일급';

    const calcTotal = (salary: number) =>
        isHourly ? salary * dailyHours * totalDays : salary * totalDays;

    const totalSalary = negotiable
        ? (proposedSalary ? calcTotal(parseInt(proposedSalary)) : 0)
        : calcTotal(currentSalary);

    return ReactDOM.createPortal(
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content" style={{ backgroundColor: 'white' }}>
                    <div className="modal-body">
                        <div className="round-box orange">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">현재 급여</span>
                                    <span className="cont small-number">{salaryLabel} {currentSalary.toLocaleString()}원</span>
                                </div>
                            </div>
                        </div>

                        {negotiable && (
                            <form className="form-box modal-form mt20">
                                <div className="form-item">
                                    <label className="input-tit">제안 급여</label>
                                    <div className="line-input-box">
                                        <input
                                            type="number"
                                            placeholder="금액을 입력하세요"
                                            value={proposedSalary}
                                            onChange={(e) => setProposedSalary(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            </form>
                        )}

                        <div className="round-box mt20 gray">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">총 기간</span>
                                    <span className="cont">
                                        {totalDays}일
                                        {isHourly && dailyHours > 0 && ` (총 ${Math.floor(totalDays * dailyHours)}시간)`}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="label">총 예상 급여</span>
                                    <span className="cont">{totalSalary.toLocaleString()}원</span>
                                </div>
                            </div>
                        </div>

                        <div className="modal-info-p">
                            <strong>공고에 지원하시겠습니까?</strong>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <div className="modal_btn">
                            <button type="button" className="btn cancel" onClick={onClose}>닫기</button>
                            <button
                                type="button"
                                className="btn confirm"
                                onClick={async () => {
                                    if (isSubmittingRef.current) return; // 즉시 차단

                                    if (negotiable) {
                                        if (!proposedSalary) {
                                            defaultAlert('급여를 입력해주세요.');
                                            return;
                                        }
                                        const rawAmount = Number(proposedSalary);
                                        if (isNaN(rawAmount) || rawAmount <= 0) {
                                            defaultAlert('지원 금액은 0원보다 커야 합니다.');
                                            return;
                                        }
                                        if (!Number.isInteger(rawAmount)) {
                                            defaultAlert('지원 금액은 원 단위 정수로 입력해주세요.');
                                            return;
                                        }
                                        if (hasRange && (rawAmount < minAllowed || rawAmount > maxAllowed)) {
                                            defaultAlert(`제안 금액은 ${minAllowed.toLocaleString()}원 ~ ${maxAllowed.toLocaleString()}원 사이여야 합니다.`);
                                            return;
                                        }
                                    }

                                    isSubmittingRef.current = true;
                                    setIsSubmitting(true);
                                    try {
                                        await onConfirm(negotiable ? proposedSalary : String(currentSalary));
                                    } finally {
                                        isSubmittingRef.current = false;
                                        setIsSubmitting(false);
                                    }
                                }}
                                disabled={isSubmitting}
                            >
                                지원하기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
};